// The interaction PROGRAM LIBRARY — a 1:1 port of age-of-mcp's
// `station-programs.ts`. Each age-of-mcp program is a pure pose function
// `(t) -> WorkPose` (a sinusoidal motion of the whole avatar); here we sample
// that exact math across one loop and emit it as keyframed {@link Track}s that
// drive the actor's `root` node (and, for the seated program, its legs). The
// curve the worker follows is therefore identical to the game's — the building
// still "decides" the motion by choosing a program and a station; this module
// only implements HOW each program moves.
//
// Faithfulness note: age-of-mcp moves the whole avatar (a WorkPose of
// dx/dy/lift/pitch/yaw/squash) and swaps to a baked seated mesh for sit-eat. We
// have no seated mesh, so sit-eat is approximated by bending the legs forward;
// every other program is pure root motion, exactly as in the game. The pose is
// authored in the actor's LOCAL frame (station.facing turns it to the work and
// station.position places it), so the per-pose `st.yaw`/offset terms collapse to
// "forward = +z", leaving the program's own reach/lean/lift.

import type { Keyframe, Track, Vec3 } from "./types";

const TAU = Math.PI * 2;

/** A ready-to-use interaction body: a loop duration plus the actor tracks. Spread
 *  into an interaction: `{ id, label, station, ...benchWork() }`. */
export interface Program {
  duration: number;
  tracks: Track[];
}

/** The full transient pose, in the actor's local frame (forward = +z). */
interface Pose {
  dx: number; dy: number; lift: number; pitch: number; yaw: number; squash: number;
}

function smoothstep(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}

// Sample a pose function (taking ms) across `duration` seconds into a `root`
// track. Dense linear sampling reproduces the sine curves to the eye.
function rootTrack(duration: number, steps: number, pose: (ms: number) => Pose): Track {
  const position: Keyframe<Vec3>[] = [];
  const rotation: Keyframe<Vec3>[] = [];
  const scale: Keyframe<number | Vec3>[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (duration * i) / steps;
    const p = pose(t * 1000);
    position.push({ t, value: [p.dx, p.lift, p.dy] });
    rotation.push({ t, value: [p.pitch, p.yaw, 0] });
    scale.push({ t, value: [1, p.squash, 1] });
  }
  return { subject: "actor", target: "root", position, rotation, scale };
}

// ── bench-work — working at a building's bench / oven / saw ───────────────────
// On the up-beat the avatar RISES; on the down-beat it STRIKES (leans, reaches
// forward and crouches), with a slow lateral shuffle along the bench. Periods
// nudged to a common 2.56s loop (strike 0.64 ×4, shuffle ≈2.56) so it cycles
// seamlessly. (age-of-mcp constants: STRIKE 640, CRAFT_REACH .14, RISE .11,
// SHUFFLE .12, PITCH_BASE .10 + SWING .18, SQUASH .10.)
export function benchWork(): Program {
  const duration = 2.56;
  return {
    duration,
    tracks: [rootTrack(duration, 64, (t) => {
      const up = Math.sin((t / 640) * TAU);
      const rise = Math.max(0, up), strike = Math.max(0, -up);
      const lat = Math.sin((t / 2560) * TAU) * 0.12;
      return {
        dx: -lat,                       // shuffle along the bench (perpendicular)
        dy: 0.14 * strike,              // reach forward into the work on the strike
        lift: 0.11 * rise,              // lift on the up-beat
        pitch: 0.10 + 0.18 * strike,    // lean into the strike
        yaw: 0,
        squash: 1 - 0.10 * strike,      // crouch into the strike
      };
    })],
  };
}

// ── sit-eat — seated at a table, eating ──────────────────────────────────────
// Seated, facing the table, with a periodic forward NOD over the food that comes
// and goes in slow appetite waves. Approximated without a seated mesh: the legs
// bend forward and the root drops onto the bench. (age-of-mcp: NOD 1300, WAVE
// 5200, PITCH_BASE .06 + NOD .16, REACH .03.)
const SIT_DROP = 0.13;        // lower the perched figure so it rests on the seat
const SIT_LEG = 1.35;          // forward leg bend (radians) that reads as seated
const SIT_HIP_L: Vec3 = [-0.05, 0.16, 0];
const SIT_HIP_R: Vec3 = [0.05, 0.16, 0];
export function sitEat(): Program {
  const duration = 5.2;
  const legKeys: Keyframe<Vec3>[] = [{ t: 0, value: [SIT_LEG, 0, 0] }, { t: duration, value: [SIT_LEG, 0, 0] }];
  return {
    duration,
    tracks: [
      rootTrack(duration, 52, (t) => {
        const wave = 0.5 + 0.5 * Math.sin((t / 5200) * TAU);
        const gate = smoothstep((wave - 0.25) / 0.5);
        const nod = Math.max(0, Math.sin((t / 1300) * TAU)) * gate;
        return {
          dx: 0,
          dy: 0.03 * nod,                 // small reach over the food on the nod
          lift: -SIT_DROP,                // rest on the seat (station lifts to seat top)
          pitch: 0.06 + 0.16 * nod,
          yaw: 0,
          squash: 1,
        };
      }),
      { subject: "actor", target: "leg-l", pivot: SIT_HIP_L, rotation: legKeys },
      { subject: "actor", target: "leg-r", pivot: SIT_HIP_R, rotation: legKeys },
    ],
  };
}

// ── pace — walk a waypoint loop (the farm's crop rows) ────────────────────────
// A slow, unhurried stroll along the loop the station declares, at a constant
// 0.45 tiles/s, facing along the path. (age-of-mcp PACE_SPEED .45.)
const PACE_SPEED = 0.45;
export function pace(waypoints: [number, number][]): Program {
  const n = waypoints.length;
  const seg: number[] = [];
  let total = 0;
  for (let i = 0; i < n; i++) {
    const a = waypoints[i]!, b = waypoints[(i + 1) % n]!;
    const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
    seg.push(l); total += l;
  }
  const duration = total > 0 ? total / PACE_SPEED : 1;
  return {
    duration,
    tracks: [rootTrack(duration, Math.max(64, n * 12), (ms) => {
      let d = ((ms / 1000) * PACE_SPEED) % total;
      let i = 0;
      while (i < n - 1 && d > seg[i]!) { d -= seg[i]!; i++; }
      const a = waypoints[i]!, b = waypoints[(i + 1) % n]!;
      const f = seg[i]! > 1e-6 ? d / seg[i]! : 0;
      const vx = b[0] - a[0], vy = b[1] - a[1];
      return {
        dx: a[0] + vx * f, dy: a[1] + vy * f, lift: 0, pitch: 0,
        yaw: Math.hypot(vx, vy) > 1e-6 ? Math.atan2(vx, vy) : 0, squash: 1,
      };
    })],
  };
}

// ── lean-load — loading / unloading at a container ───────────────────────────
// A quiet bend over the container in a slow rhythm. (age-of-mcp LOAD 1100,
// PITCH_BASE .16 + BEND .16, REACH .05.)
export function leanLoad(): Program {
  const duration = 1.1;
  return {
    duration,
    tracks: [rootTrack(duration, 24, (t) => {
      const bend = 0.5 + 0.5 * Math.sin((t / 1100) * TAU);
      return { dx: 0, dy: 0.05 * bend, lift: 0, pitch: 0.16 + 0.16 * bend, yaw: 0, squash: 1 - 0.04 * bend };
    })],
  };
}

// ── climb — going up a structure (the tower) ─────────────────────────────────
// The avatar rises up the face (a clamber up, a pause at the top, a climb back
// down — the ease age-of-mcp's caller applies, synthesised here as one loop)
// with a side-to-side clambering wiggle. (age-of-mcp CLIMB_RISE 1.0, WIGGLE .10,
// CLIMB_MS 360.)
export function climb(): Program {
  const duration = 3.6;
  return {
    duration,
    tracks: [rootTrack(duration, 72, (ms) => {
      const t = ms / 1000;
      const p = t / duration;
      // up by 40%, hold to 60%, back down — eased.
      const shape = p < 0.4 ? smoothstep(p / 0.4) : p < 0.6 ? 1 : smoothstep((1 - p) / 0.4);
      const wig = Math.sin((ms / 360) * TAU);
      return { dx: -0.10 * wig, dy: 0, lift: 1.0 * shape, pitch: 0.14 + 0.06 * wig, yaw: 0, squash: 1 };
    })],
  };
}

// ── stand-visit — a small beat at an entity (an instant interact) ─────────────
// Face the entity and give a gentle acknowledging beat. (age-of-mcp VISIT 700,
// AMP .05, PITCH_BASE .05 + .08.)
export function standVisit(): Program {
  const duration = 0.7;
  return {
    duration,
    tracks: [rootTrack(duration, 16, (t) => {
      const beat = 0.5 - 0.5 * Math.cos((t / 700) * TAU);
      return { dx: 0, dy: 0.05 * beat, lift: 0, pitch: 0.05 + 0.08 * beat, yaw: 0, squash: 1 - 0.04 * beat };
    })],
  };
}

// ── swing — striking a resource (chop / mine) ────────────────────────────────
// Rock toward the target and back with a lean and a slight crouch on each blow.
// (age-of-mcp SWING_AMP .09, SWING_MS 520, PITCH_BASE .06 + .11, SQUASH .06.)
// Buildings use bench-work; this serves a resource node a station may point at.
export function swing(): Program {
  const duration = 0.52;
  const reach = 0.18;
  return {
    duration,
    tracks: [rootTrack(duration, 16, (t) => {
      const phase = (t / 520) * TAU;
      const rock = (Math.sin(phase) * 0.5 - 0.5) * Math.min(0.09, reach * 0.5);
      const thrust = 0.5 - 0.5 * Math.cos(phase);
      return {
        dx: 0, dy: reach + rock, lift: 0,
        pitch: 0.06 + 0.11 * thrust, yaw: 0, squash: 1 - 0.06 * thrust,
      };
    })],
  };
}
